import type { EventTopic, SseEvent } from '@warehouse/shared';
import { sseEventSchema } from '@warehouse/shared';

import { apiUrl } from '../lib/api-client';

/**
 * ONE EventSource for the whole tab.
 *
 * `GET /api/events/stream?topics=a,b` (apps/api/src/core/events/events.controller.ts)
 * delivers durable `event_log` rows. We keep the UNION of every mounted
 * subscriber's topics: a dashboard with six live lists must not open six
 * streams (nginx holds each open for an hour and browsers cap ~6 connections
 * per origin — seven live widgets would deadlock every other request on the
 * page).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * Why reconnection is MANUAL instead of letting EventSource do it
 * ─────────────────────────────────────────────────────────────────────────────
 * NestJS's `SseStream.writeMessage` stamps an auto-incrementing `id:` on any
 * message that does not carry one — including the 25-second `event: ping`
 * heartbeat. The browser records `id:` from EVERY event regardless of type, so
 * after two minutes idle its internal Last-Event-ID is `5` (a ping counter),
 * not the `event_log` row it last saw. Its automatic reconnect would then send
 * `Last-Event-ID: 5`, and the api — which prefers the header over the query
 * param — would replay every row above 5: on a live system, the entire log,
 * every reconnect, forever.
 *
 * So we close the socket on error and reopen it ourselves with backoff. A
 * freshly constructed EventSource sends no `Last-Event-ID` header, which leaves
 * our own `?lastEventId=` — tracked ONLY from real `message` events — as the
 * cursor the server sees. Same replay guarantee, without trusting a heartbeat
 * counter to be a database id.
 */

export type ConnectionState = 'connecting' | 'open' | 'closed';

type EventListener = (event: SseEvent) => void;
type StateListener = (state: ConnectionState) => void;

interface Subscription {
  topics: readonly EventTopic[];
  listener: EventListener;
}

/** Reopening on every mount in a burst would thrash the api; batch them. */
const REOPEN_DEBOUNCE_MS = 50;
/** Backoff for manual reconnects: 1s, 2s, 4s … capped, plus jitter. */
const RETRY_BASE_MS = 1_000;
const RETRY_MAX_MS = 30_000;

class EventStreamClient {
  private source: EventSource | null = null;
  private readonly subscriptions = new Set<Subscription>();
  private readonly stateListeners = new Set<StateListener>();
  private currentTopics: string[] = [];
  private lastEventId: string | null = null;
  private state: ConnectionState = 'closed';
  private reopenTimer: ReturnType<typeof setTimeout> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private retryAttempt = 0;

  subscribe(topics: readonly EventTopic[], listener: EventListener): () => void {
    const subscription: Subscription = { topics, listener };
    this.subscriptions.add(subscription);
    this.scheduleSync();
    return () => {
      this.subscriptions.delete(subscription);
      this.scheduleSync();
    };
  }

  onStateChange(listener: StateListener): () => void {
    this.stateListeners.add(listener);
    listener(this.state);
    return () => {
      this.stateListeners.delete(listener);
    };
  }

  getState(): ConnectionState {
    return this.state;
  }

  /** Test/teardown seam. */
  close(): void {
    if (this.reopenTimer) clearTimeout(this.reopenTimer);
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.reopenTimer = null;
    this.retryTimer = null;
    this.retryAttempt = 0;
    this.source?.close();
    this.source = null;
    this.currentTopics = [];
    this.setState('closed');
  }

  private scheduleSync(): void {
    if (this.reopenTimer) clearTimeout(this.reopenTimer);
    this.reopenTimer = setTimeout(() => {
      this.reopenTimer = null;
      this.sync();
    }, REOPEN_DEBOUNCE_MS);
  }

  private sync(): void {
    const wanted = this.unionTopics();

    if (wanted.length === 0) {
      if (this.source || this.retryTimer) {
        if (this.retryTimer) clearTimeout(this.retryTimer);
        this.retryTimer = null;
        this.retryAttempt = 0;
        this.source?.close();
        this.source = null;
        this.currentTopics = [];
        this.setState('closed');
      }
      return;
    }

    if (this.source && sameTopics(wanted, this.currentTopics)) return;

    this.source?.close();
    this.currentTopics = wanted;
    this.retryAttempt = 0;
    this.open();
  }

  private scheduleRetry(): void {
    if (this.retryTimer || this.currentTopics.length === 0) return;
    const delay = Math.min(RETRY_BASE_MS * 2 ** this.retryAttempt, RETRY_MAX_MS);
    this.retryAttempt += 1;
    // Jitter: 50 tablets losing the same AP must not reconnect in lockstep.
    const jittered = delay * (0.75 + Math.random() * 0.5);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (this.currentTopics.length > 0) this.open();
    }, jittered);
  }

  private open(): void {
    if (typeof EventSource === 'undefined') return;

    const url = apiUrl('/events/stream', {
      topics: this.currentTopics.join(','),
      ...(this.lastEventId ? { lastEventId: this.lastEventId } : {}),
    });

    this.setState('connecting');
    // `withCredentials` is what sends the httpOnly session cookie on the stream.
    const source = new EventSource(url, { withCredentials: true });
    this.source = source;

    source.onopen = () => {
      this.retryAttempt = 0;
      this.setState('open');
    };

    source.onerror = () => {
      // EventSource cannot expose the status code, so we cannot tell "api
      // restarting" from "session revoked" here. We close and back off; a
      // session that is genuinely gone surfaces on the next REST call, which
      // owns the refresh-or-redirect decision.
      if (this.source !== source) return; // superseded by a topic change
      source.close();
      this.source = null;
      this.setState('connecting');
      this.scheduleRetry();
    };

    source.onmessage = (message: MessageEvent<string>) => {
      if (message.lastEventId) this.lastEventId = message.lastEventId;
      const event = parseEvent(message.data);
      if (!event) return;
      for (const subscription of [...this.subscriptions]) {
        if (subscription.topics.length === 0 || subscription.topics.includes(event.topic)) {
          subscription.listener(event);
        }
      }
    };
  }

  private unionTopics(): string[] {
    const topics = new Set<string>();
    for (const subscription of this.subscriptions) {
      for (const topic of subscription.topics) topics.add(topic);
    }
    return [...topics].sort();
  }

  private setState(state: ConnectionState): void {
    if (this.state === state) return;
    this.state = state;
    for (const listener of [...this.stateListeners]) listener(state);
  }
}

function sameTopics(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((topic, index) => topic === b[index]);
}

function parseEvent(data: string): SseEvent | null {
  try {
    const parsed = sseEventSchema.safeParse(JSON.parse(data));
    // A heartbeat (`event: ping`) never reaches onmessage, but an unknown topic
    // from a newer server would — dropping it is correct, not an error.
    return parsed.success ? (parsed.data as SseEvent) : null;
  } catch {
    return null;
  }
}

export const eventStream = new EventStreamClient();
